/**
 * Lightweight daily view counter for BSF Analytics KPI tracking.
 *
 * Deduplicates per video per page load using a Set.
 * Batches counts and sends a single sendBeacon on page unload.
 * Only active when user has opted in (prestoPlayer.trackViews).
 *
 */

const hookNamespace = 'presto-player.daily-views';
const trackedVideos = new Set();
let beaconRegistered = false;
let beaconSent = false;

/**
 * Send the batched view count via sendBeacon.
 *
 * Guards against missing sendBeacon API (older browsers/WebViews)
 * and prevents duplicate sends when both visibilitychange and
 * pagehide fire on the same unload.
 */
function sendViewCount() {
  if (beaconSent || trackedVideos.size === 0) {
    return;
  }

  if (typeof navigator.sendBeacon !== 'function') {
    return;
  }

  const url = window?.prestoPlayer?.ajaxurl;
  if (!url) {
    return;
  }

  const formData = new FormData();
  formData.append('action', 'presto_player_daily_views');
  formData.append('count', String(trackedVideos.size));

  const queued = navigator.sendBeacon(url, formData);
  if (queued) {
    trackedVideos.clear();
    beaconSent = true;
  }
}

/**
 * Register page unload listeners (once per page load).
 */
function registerBeacon() {
  if (beaconRegistered) {
    return;
  }
  beaconRegistered = true;

  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') {
      sendViewCount();
    }
    // Reset sent flag when page becomes visible again (back/forward cache).
    if (document.visibilityState === 'visible') {
      beaconSent = false;
    }
  });

  // pagehide is more reliable on mobile Safari.
  window.addEventListener('pagehide', sendViewCount);
}

/**
 * Handle a video play event.
 * Deduplicates by player config ID per page load.
 *
 * @param {Object} player - The Presto Player instance.
 */
function onPlay(player) {
  const videoId = player?.config?.id;
  if (!videoId || trackedVideos.has(videoId)) {
    return;
  }

  trackedVideos.add(videoId);
}

/**
 * Initialize view tracking for a player instance.
 *
 * @param {Object} player - The Presto Player instance.
 */
export default function viewTracking(player) {
  if (!window?.prestoPlayer?.trackViews) {
    return;
  }

  if (!window?.wp?.hooks) {
    return;
  }

  registerBeacon();

  if (!wp.hooks.hasAction('presto.playerPlay', hookNamespace)) {
    wp.hooks.addAction('presto.playerPlay', hookNamespace, onPlay);
  }
}
